import { Add } from '@mui/icons-material';
import { Box, Button, LinearProgress, Typography, useTheme } from '@mui/material';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AiSearch from '../../../components/AiSearch/AiSearch';
import ActionBar from '../../../components/ActionBar/ActionBar';
import ContentContainer from '../../../components/Containers/ContentContainer';
import LeftContainer from '../../../components/Containers/LeftContainer';
import Section from '../../../components/Main/Section/Section';
import { AuthContext } from '../../../providers/AuthProvider';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import AiSearchMonitoringService from '../../../services/AiSearchMonitoringService';
import { IAiSearchQuestion } from '../../../shared/interfaces/IAiSearchQuestion';
import { useTranslation } from 'react-i18next';

const AiSearches: FC = () => {
  const { setCurrentView } = useContext(CurrentViewContext);
  const { hasRole, exceedsLimit } = useContext(AuthContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<IAiSearchQuestion[]>([]);

  useEffect(() => {
    setCurrentView(t('nav_ai_search'));
    document.title = `${t('app_branding')} ${t('nav_ai_search')}`;
  }, [setCurrentView, t]);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const response = await AiSearchMonitoringService.getMonitoring(setLoading, controller.signal);
      if (Array.isArray(response)) setQuestions(response);
    })();
    return () => controller.abort();
  }, []);

  const onDelete = (id: string) =>
    setQuestions((current) => current.filter((question) => question.id !== id));
  const canAdd = hasRole('can-add') && !exceedsLimit('limitaisearch', questions.length + 1);

  return (
    <Section>
      {questions.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            borderRadius: `${theme.shape.borderRadius}px`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography>{t('no_records')}</Typography>
          {canAdd && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('guide')}
              sx={{ mt: theme.spacing(2) }}
            >
              {t('ai_search_add_question')}
            </Button>
          )}
        </Box>
      ) : (
        <>
          {loading ? (
            <LinearProgress />
          ) : (
            <ContentContainer>
              <LeftContainer>
                <ActionBar>
                  {canAdd && (
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => navigate('guide')}
                      sx={{ ml: 'auto' }}
                    >
                      {t('ai_search_add_question')}
                    </Button>
                  )}
                </ActionBar>
                <Typography
                  variant="subtitle2"
                  sx={{ p: theme.spacing(1) }}
                >
                  {questions.length} {t('ai_search_questions')}
                </Typography>
                {questions.map((question) => (
                  <AiSearch
                    key={question.id}
                    id={question.id}
                    query={question.query}
                    searchHash={question.searchHash}
                    country={question.country}
                    date={question.createdAt}
                    onDelete={onDelete}
                  />
                ))}
              </LeftContainer>
            </ContentContainer>
          )}
        </>
      )}
    </Section>
  );
};

export default AiSearches;
